"use client";

import { useTranslations } from "next-intl";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { IconCheck } from "@/components/icons";
import { cardTitleSx, priceSx, pricingCardSx } from "@/theme/tokens";
import { selectableHandlers } from "@/lib/interaction";
import { StartCta } from "./StartCta";
import { CareAddOn } from "./CareAddOn";
import { Checklist } from "./Checklist";
import { PACKAGE_KEYS, usePricingSelection } from "./PricingSelectionContext";

/**
 * The interactive part of the pricing section. The package cards are a
 * single-choice radio group, each with its own "Get Started" CTA that deep-links
 * to the onboarding assistant with that package (and the chosen Care plan)
 * prefilled. The Care & support add-on sits in the same row as a fourth card.
 * Selection lives in the shared PricingSelectionContext. Client component.
 */
export function PricingCards() {
  const t = useTranslations("pricing");
  const { pkg, selectPackage, care } = usePricingSelection();
  // Each tier/plan lists exactly three short feature bullets (features.0–2).
  const featureList = (prefix: string) =>
    [0, 1, 2].map((i) => t(`${prefix}.features.${i}`));

  return (
    <Box
      sx={{
        display: "grid",
        gap: { xs: 3, md: 4 },
        // One column on phones, two on small screens, all cards (the packages
        // plus the Care add-on) in a single row on large screens.
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          lg: `repeat(${PACKAGE_KEYS.length + 1}, 1fr)`,
        },
        alignItems: "stretch",
      }}
    >
      {/* The package cards keep their radiogroup semantics; `display: contents`
          lets them flow as cells of the grid above, beside the Care card. */}
      <Box
        role="radiogroup"
        aria-label={t("selectLabel")}
        sx={{ display: "contents" }}
      >
        {PACKAGE_KEYS.map((key) => {
          const prefix = `tiers.${key}`;
          const features = featureList(prefix);
          const isSelected = key === pkg;
          const name = t(`${prefix}.name`);
          return (
            <Paper
              key={key}
              elevation={0}
              role="radio"
              tabIndex={0}
              aria-checked={isSelected}
              aria-label={name}
              {...selectableHandlers(() => selectPackage(key))}
              sx={{
                ...pricingCardSx(isSelected),
                position: "relative",
                cursor: "pointer",
                outlineOffset: 2,
              }}
            >
              {/* Selected badge — top-right check, only on the active card. */}
              {isSelected ? (
                <Box
                  aria-hidden
                  sx={{
                    position: "absolute",
                    top: 14,
                    right: 14,
                    display: "grid",
                    placeItems: "center",
                    color: "primary.main",
                  }}
                >
                  <IconCheck size={20} />
                </Box>
              ) : null}

              <Typography variant="h3" sx={{ ...cardTitleSx, pr: 4 }}>
                {name}
              </Typography>
              <Typography sx={priceSx}>{t(`${prefix}.price`)}</Typography>
              <Typography
                color="text.secondary"
                sx={{ fontSize: "0.8rem", mb: 2 }}
              >
                {t(`${prefix}.per`)}
              </Typography>
              <Typography sx={{ fontWeight: 600, mb: 2.5 }}>
                {t(`${prefix}.tagline`)}
              </Typography>
              <Checklist items={features} />
              <StartCta
                variant={isSelected ? "contained" : "outlined"}
                href={`/start?package=${key}${care !== "none" ? `&care=${care}` : ""}`}
                onClick={(e) => e.stopPropagation()}
                sx={{ mt: "auto", py: 1.1 }}
              >
                {t("cta")}
              </StartCta>
            </Paper>
          );
        })}
      </Box>

      {/* Care & support: the recurring add-on, as the fourth card in the row. */}
      <CareAddOn />
    </Box>
  );
}
